// Step 1 — the wizard's front door (2026-08-10 redesign).
//
// This surface used to be a pure "cinematic splash" that led into a SEPARATE
// page which forced the customer into the floor planner before we knew who
// they were. The two are now merged: this is a welcome to the customer's own
// portal, and the only thing it asks for is a first name and an email.
//
// Why the popup, and why it opens on click:
//   The wizard's real job is capturing contact details. Style, finish, colour
//   and room dimensions can't be gathered reliably through a form — they need
//   a conversation. So we ask for the two fields that matter as early as
//   possible, and treat everything after this as optional.
//   The popup is CTA-triggered, never on load: an unprompted modal in front of
//   a customer who hasn't decided to start yet just gets dismissed.
//
// Auth posture is enforced server-side in wizard_submit.js's /public/wizard-start
// (read the comment block there before changing this). In short: a brand-new
// email creates the account; a KNOWN email is never signed in from this form —
// they get a magic link emailed instead. Nothing here grants portal access.

import type { RetailerBranding } from "../types";
import { loadTurnstile, TURNSTILE_SITE_KEY } from "./turnstile";

/** Final fallback if neither the retailer's own images nor the range gallery
 *  yield anything (or both fail to load). */
const FALLBACK_HERO = "https://dev.tkos.io/uploads/ai-render-tests/splash-hero.webp";

/** What the customer gave us in the popup. */
export interface SplashCapture {
  firstName: string;
  email: string;
  /** True when the email already had a portal account — we emailed them a
   *  magic link rather than signing them in. See wizard-start. */
  existing: boolean;
}

function escAttr(s: string): string {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function escText(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// ── Icons ────────────────────────────────────────────────────────────────────
// Hand-rolled inline SVG, 24×24, 1.5px stroke — same set/weight as the rest of
// the wizard. No icon library (see the house design rules).

function icon(paths: string): string {
  return `<svg class="wiz-splash__step-icon" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true">${paths}</svg>`;
}

/** Room outline with a doorway — "tell us about your space". */
const ICON_SPACE = icon(
  `<path d="M3 5.5h18v13H3z"/><path d="M3 12h5"/><path d="M14 18.5v-6h7"/>`,
);
/** Pencil over a rule — "we'll design it". */
const ICON_DESIGN = icon(
  `<path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17v3z"/><path d="M14.5 6.5l3 3"/>`,
);
/** Price tag with a conversation dot — "prices & meet your retailer". */
const ICON_PRICE = icon(
  `<path d="M3.5 12.5V4.5h8l8.5 8.5-8 8-8.5-8.5z"/><circle cx="8" cy="9" r="1.4"/>`,
);

const STEPS: Array<{ icon: string; label: string }> = [
  { icon: ICON_SPACE,  label: "Tell us about your space" },
  { icon: ICON_DESIGN, label: "We'll design it" },
  { icon: ICON_PRICE,  label: "Get prices &amp; meet your retailer" },
];

// ── Hero image resolution ────────────────────────────────────────────────────

interface RangeRow {
  supplierId?: string;
  status?: string;
  heroImage?: string;
  portfolioImages?: Array<{ url?: string }>;
}

/** Photography from the retailer's Style & Colour door ranges — the default
 *  hero source, so a retailer gets a real kitchen behind their headline with
 *  nothing to set up. Never throws; returns [] if anything goes wrong.
 *
 *  SCOPED TO THE RETAILER (2026-08-27, Steve). Without `?retailer=` the
 *  endpoint returns every published range across all suppliers, so a retailer's
 *  front door could open on a kitchen from a range they do not sell. Same
 *  parameter and same reasoning as `loadRanges()` in range-browser.ts: the
 *  server decides from the wizard grants, and a retailer with no curated
 *  selection still gets everything published, so nothing changes for them. */
async function galleryHeroImages(retailerId?: string): Promise<string[]> {
  try {
    const qs = retailerId ? `?retailer=${encodeURIComponent(retailerId)}` : "";
    const res = await fetch(`/api/door-ranges${qs}`, {
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { ranges?: RangeRow[] };
    const rows = Array.isArray(body.ranges) ? body.ranges : [];
    const out: string[] = [];
    for (const r of rows) {
      // Same live/approved filter step 2b applies — never surface a draft
      // range's photography on the front door.
      if (r.status && r.status !== "live" && r.status !== "approved") continue;
      for (const raw of [r.heroImage, ...(r.portfolioImages ?? []).map((p) => p?.url)]) {
        if (typeof raw === "string" && raw && !out.includes(raw)) out.push(raw);
      }
    }
    return out;
  } catch {
    return [];
  }
}

/** Pick the hero for this page load. Custom uploads win when configured;
 *  otherwise the range gallery, narrowed to the retailer's picks if they made
 *  any. Rotates at random so a repeat visitor doesn't always see the same shot. */
async function resolveHero(branding: RetailerBranding, retailerId?: string): Promise<string> {
  const pick = (list: string[]): string =>
    list.length ? list[Math.floor(Math.random() * list.length)]! : "";

  if (branding.heroImageSource === "custom" && branding.heroImages.length) {
    return pick(branding.heroImages);
  }
  const gallery = await galleryHeroImages(retailerId);
  if (branding.heroImages.length) {
    // Retailer narrowed the gallery to specific shots. Honour that, but only
    // for images the catalogue still serves — a since-deleted range shouldn't
    // leave them with a broken hero.
    const chosen = branding.heroImages.filter((u) => gallery.includes(u));
    if (chosen.length) return pick(chosen);
  }
  return pick(gallery) || FALLBACK_HERO;
}

// ── Popup: first name + email ────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Opens the capture popup. Resolves with the details once the customer is
 *  through, or null if they dismissed it. */
function openCapturePopup(
  branding: RetailerBranding,
  retailerId: string,
): Promise<SplashCapture | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "wiz-capture";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "wiz-capture-title");
    overlay.innerHTML = `
      <div class="wiz-capture__panel">
        <button type="button" class="wiz-capture__close" data-role="close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>

        <div data-role="form-view">
          <h2 class="wiz-capture__title" id="wiz-capture-title">First, who are we designing for?</h2>
          <p class="wiz-capture__sub">Just your name and email — we'll set up your own kitchen portal so you can pick this up any time.</p>

          <label class="wiz-capture__label" for="wiz-cap-name">First name</label>
          <input class="wiz-capture__input" id="wiz-cap-name" type="text" autocomplete="given-name"
                 maxlength="120" data-field="firstName" />
          <p class="wiz-capture__err" data-err="firstName"></p>

          <label class="wiz-capture__label" for="wiz-cap-email">Email address</label>
          <input class="wiz-capture__input" id="wiz-cap-email" type="email" autocomplete="email"
                 inputmode="email" maxlength="200" data-field="email" />
          <p class="wiz-capture__err" data-err="email"></p>

          <!-- Honeypot: hidden from people, irresistible to bots. -->
          <input class="wiz-capture__hp" type="text" data-field="website" tabindex="-1"
                 autocomplete="off" aria-hidden="true" />

          <button type="button" class="wiz-capture__submit" data-role="submit">
            ${escText(branding.splashCtaLabel)}
          </button>
          <p class="wiz-capture__err wiz-capture__err--form" data-err="form"></p>
          <p class="wiz-capture__legal">We'll only use these to set up your portal and get in touch about your kitchen.</p>
          <div data-role="turnstile" class="wiz-capture__turnstile"></div>
        </div>

        <!-- Returning customer. Verified with their PASSWORD against the
             platform's existing /api/customer/login — the wizard does not run
             its own auth. Only shown when the server confirms a portal login
             actually exists (hasAccount), so we never ask for a password that
             was never set. -->
        <div data-role="pw-view" hidden>
          <h2 class="wiz-capture__title">Welcome back</h2>
          <p class="wiz-capture__sub" data-role="pw-msg"></p>

          <label class="wiz-capture__label" for="wiz-cap-pw">Password</label>
          <input class="wiz-capture__input" id="wiz-cap-pw" type="password"
                 autocomplete="current-password" maxlength="200" data-field="password" />
          <p class="wiz-capture__err" data-err="password"></p>

          <button type="button" class="wiz-capture__submit" data-role="pw-submit">
            Sign in &amp; continue
          </button>
          <button type="button" class="wiz-capture__linkbtn" data-role="forgot">
            Forgot your password?
          </button>
          <button type="button" class="wiz-capture__secondary" data-role="pw-skip">
            Carry on without signing in
          </button>
        </div>

        <!-- Reset link sent, or a recognised lead with no login yet. -->
        <div data-role="note-view" hidden>
          <h2 class="wiz-capture__title" data-role="note-title"></h2>
          <p class="wiz-capture__sub" data-role="note-msg"></p>
          <button type="button" class="wiz-capture__submit" data-role="note-continue">Carry on planning</button>
        </div>
      </div>
    `;

    const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
    const field = (name: string): HTMLInputElement =>
      overlay.querySelector<HTMLInputElement>(`[data-field="${name}"]`)!;
    const errEl = (name: string): HTMLElement =>
      overlay.querySelector<HTMLElement>(`[data-err="${name}"]`)!;
    const setErr = (name: string, msg: string): void => {
      const el = errEl(name);
      el.textContent = msg;
      el.classList.toggle("is-shown", !!msg);
    };

    let settled = false;
    const finish = (value: SplashCapture | null): void => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      resolve(value);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") finish(null);
    };

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) finish(null);
    });
    q<HTMLButtonElement>("[data-role=close]").addEventListener("click", () => finish(null));

    const submitBtn = q<HTMLButtonElement>("[data-role=submit]");

    // Turnstile: same invisible interaction-only widget the final submit uses,
    // so this endpoint isn't a free account-creation faucet for a bot pool.
    //
    // 2026-08-29: this popup used to render the widget and then, at click time,
    // poll blindly for a token for 4s — the exact race step 4 diagnosed and
    // fixed on 2026-08-03. It shipped an empty token, the server rejected it
    // with a 400, and because nothing ever reset the errored widget the
    // "please try again" it displayed could never come true. Port step 4's
    // machinery: gate the button on token readiness, and reset() the widget on
    // error/expiry so a retry has a fresh token to collect.
    let turnstileToken = "";
    let turnstileWidgetId: string | undefined;
    const ctaLabel = (submitBtn.textContent || "").trim();
    const setSubmitReady = (ready: boolean): void => {
      submitBtn.disabled = !ready;
      submitBtn.textContent = ready ? ctaLabel : "Verifying…";
    };
    setSubmitReady(false);

    // The gate must never become the trap the 400 was. If Turnstile is blocked,
    // slow, or stuck in an error loop, open the button anyway: the capture call
    // fails open on a bad token, and step 4 remains the real gate.
    window.setTimeout(() => {
      if (overlay.isConnected) setSubmitReady(true);
    }, 8000);

    void loadTurnstile()
      .then((api) => {
        turnstileWidgetId = api.render(q<HTMLElement>("[data-role=turnstile]"), {
          sitekey: TURNSTILE_SITE_KEY,
          appearance: "interaction-only",
          callback: (token: string) => {
            turnstileToken = token;
            setSubmitReady(true);
          },
          // Previously absent: an errored or expired widget stayed dead with no
          // way to get a fresh token, so every retry re-sent the same empty one.
          "expired-callback": () => {
            turnstileToken = "";
            setSubmitReady(false);
            if (turnstileWidgetId) api.reset(turnstileWidgetId);
          },
          "error-callback": (code?: string) => {
            console.warn("[wizard] splash Turnstile error-callback", code);
            turnstileToken = "";
            setSubmitReady(false);
            if (turnstileWidgetId) api.reset(turnstileWidgetId);
          },
        });
      })
      .catch(() => {
        // Cloudflare CDN hiccup — don't strand a real customer in the popup.
        // The server still applies origin checks and rate limits.
        setSubmitReady(true);
      });

    /** Swap the popup to a one-line message with a single "carry on" button. */
    const showNote = (title: string, msg: string, result: SplashCapture): void => {
      q<HTMLElement>("[data-role=form-view]").hidden = true;
      q<HTMLElement>("[data-role=pw-view]").hidden = true;
      const note = q<HTMLElement>("[data-role=note-view]");
      note.hidden = false;
      q<HTMLElement>("[data-role=note-title]").textContent = title;
      q<HTMLElement>("[data-role=note-msg]").textContent = msg;
      const btn = q<HTMLButtonElement>("[data-role=note-continue]");
      btn.addEventListener("click", () => finish(result));
      btn.focus();
    };

    /** Returning customer: verify with their existing portal password. */
    const showPasswordView = (firstName: string, email: string): void => {
      q<HTMLElement>("[data-role=form-view]").hidden = true;
      const view = q<HTMLElement>("[data-role=pw-view]");
      view.hidden = false;
      q<HTMLElement>("[data-role=pw-msg]").textContent =
        `You already have a kitchen portal with us under ${email}. ` +
        `Enter your password to carry on where you left off.`;

      const pwInput = field("password");
      const pwBtn = q<HTMLButtonElement>("[data-role=pw-submit]");
      const forgotBtn = q<HTMLButtonElement>("[data-role=forgot]");

      const signIn = async (): Promise<void> => {
        const password = pwInput.value;
        setErr("password", "");
        if (!password) { setErr("password", "Please enter your password."); return; }

        pwBtn.disabled = true;
        const label = pwBtn.textContent;
        pwBtn.textContent = "Signing in…";
        try {
          // The platform's own customer login — rate-limited (5/min/IP) and
          // audited server-side. The wizard only needs the yes/no; it never
          // reads account data, so an ambiguous multi-retailer result
          // (needsRetailerChoice) still counts as verified and needs no
          // disambiguation here.
          const res = await fetch("/api/customer/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email, password }),
          });
          const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
          if (res.ok && body.ok) {
            finish({ firstName, email, existing: true });
            return;
          }
          if (res.status === 429) {
            setErr("password", "Too many attempts — please wait a minute and try again.");
          } else {
            setErr("password", body.error || "That password doesn't look right.");
          }
        } catch {
          setErr("password", "Couldn't reach us just now — please try again.");
        } finally {
          pwBtn.disabled = false;
          pwBtn.textContent = label;
        }
      };

      const forgot = async (): Promise<void> => {
        forgotBtn.disabled = true;
        try {
          await fetch("/api/customer/forgot-password", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          });
        } catch {
          /* The endpoint always answers ok to avoid confirming an address;
             a transport failure shouldn't say anything different. */
        }
        // Same wording either way — never reveal whether the address matched.
        showNote(
          "Check your email",
          `If ${email} has an account with us, we've sent a link to set a new password. ` +
            `You can carry on planning here in the meantime.`,
          { firstName, email, existing: true },
        );
      };

      pwBtn.addEventListener("click", () => { void signIn(); });
      forgotBtn.addEventListener("click", () => { void forgot(); });
      q<HTMLButtonElement>("[data-role=pw-skip]").addEventListener("click", () => {
        // Never trap them behind a password — the enquiry matters more than
        // the sign-in, and nothing in the wizard exposes their account anyway.
        finish({ firstName, email, existing: true });
      });
      pwInput.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); void signIn(); }
      });
      pwInput.focus();
    };

    const submit = async (): Promise<void> => {
      const firstName = field("firstName").value.trim();
      const email = field("email").value.trim().toLowerCase();
      const website = field("website").value;

      setErr("firstName", "");
      setErr("email", "");
      setErr("form", "");
      let bad = false;
      if (!firstName) { setErr("firstName", "Please tell us your first name."); bad = true; }
      if (!email) { setErr("email", "Please enter your email address."); bad = true; }
      else if (!EMAIL_RE.test(email)) { setErr("email", "That doesn't look like a valid email address."); bad = true; }
      if (bad) return;

      submitBtn.disabled = true;
      submitBtn.textContent = "Setting up…";
      try {
        const token = turnstileToken;
        const res = await fetch("/api/public/wizard-start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ retailerId, firstName, email, website, turnstileToken: token }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          existing?: boolean;
          /** True only when a customer-portal LOGIN exists for this email under
           *  this retailer — see _wizardHasPortalAccount server-side. */
          hasAccount?: boolean;
          error?: string;
        };

        // EVERY non-ok response FAILS OPEN (approved by Steve, 2026-08-29).
        // This call is an early-capture optimisation and every detail is
        // submitted again at step 4, which stays the real anti-bot gate. The
        // 400 branch that used to hold the customer here WAS the bug: a captcha
        // rejection they had no way to clear stranded them on the popup, losing
        // the whole lead to save a partial one. First name and email are
        // validated client-side above, so no reachable 400 is user-fixable.
        if (!res.ok || !data.ok) {
          console.warn(`[wizard] start capture unavailable (HTTP ${res.status}) — continuing.`);
          finish({ firstName, email, existing: false });
          return;
        }

        if (data.existing && data.hasAccount) {
          showPasswordView(firstName, email);
          return;
        }
        if (data.existing) {
          // Recognised, but they've never had a portal login — asking for a
          // password would be a dead end, and "forgot password" would send
          // nothing. Just welcome them through; they get their portal link
          // when they submit, exactly as a first-timer would.
          showNote(
            "Good to see you again",
            "We already have your details — carry on and we'll pick up from there.",
            { firstName, email, existing: true },
          );
          return;
        }

        finish({ firstName, email, existing: false });
      } catch {
        // Network-level failure — same reasoning as above, let them through.
        console.warn("[wizard] start capture request failed — continuing.");
        finish({ firstName, email, existing: false });
      }
    };

    submitBtn.addEventListener("click", () => { void submit(); });
    for (const name of ["firstName", "email"]) {
      field(name).addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); void submit(); }
      });
    }

    document.addEventListener("keydown", onKey);
    document.body.appendChild(overlay);
    field("firstName").focus();
  });
}

// ── Mount ────────────────────────────────────────────────────────────────────

/** Mount the step-1 welcome. `onStart` fires once the customer is captured and
 *  the overlay has left, handing over what they told us. */
export function mountSplash(
  host: HTMLElement,
  branding: RetailerBranding,
  retailerId: string,
  onStart: (captured: SplashCapture) => void,
): void {
  const reduce = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  // Retailer masthead — logo AND trading name together, directly above the
  // headline. This is a white-label surface: the customer should know whose
  // showroom they're in before they read anything else, and the old top-left
  // corner logo read as chrome rather than ownership. Falls back to whichever
  // of the two the retailer actually has.
  const hasLogo = !!branding.logoUrl;
  const hasName = !!branding.businessName;
  const masthead = hasLogo || hasName
    ? `<div class="wiz-splash__masthead">
        ${hasLogo ? `<img class="wiz-splash__logo" src="${escAttr(branding.logoUrl)}" alt="${escAttr(branding.businessName || "Retailer logo")}" />` : ""}
        ${hasName ? `<span class="wiz-splash__brandname">${escText(branding.businessName!)}</span>` : ""}
      </div>`
    : "";

  const steps = STEPS.map(
    (s, i) => `
      <li class="wiz-splash__step">
        <span class="wiz-splash__step-num">${i + 1}</span>
        ${s.icon}
        <span class="wiz-splash__step-label">${s.label}</span>
      </li>`,
  ).join("");

  const overlay = document.createElement("div");
  overlay.className = "wiz-splash" + (reduce ? " wiz-splash--no-motion" : "");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-label", "Welcome");
  overlay.innerHTML = `
    <div class="wiz-splash__bg" data-role="bg"></div>
    <div class="wiz-splash__scrim"></div>
    <div class="wiz-splash__content">
      ${masthead}
      <h1 class="wiz-splash__headline">${escText(branding.splashHeadline)}</h1>
      <p class="wiz-splash__sub">${escText(branding.welcomeSubtext)}</p>
      <ol class="wiz-splash__steps">${steps}</ol>
      <button type="button" class="wiz-splash__cta" data-role="start">${escText(branding.splashCtaLabel)}</button>
      <p class="wiz-splash__trust">
        <span>Takes less than 5 minutes</span>
        <span>Free, no obligation</span>
      </p>
    </div>
  `;

  // Hero loads asynchronously so it never delays the first paint. Only applied
  // once it has decoded — no flash of a broken image.
  void resolveHero(branding, retailerId).then((url) => {
    if (!url) return;
    const probe = new Image();
    probe.onload = (): void => {
      const bg = overlay.querySelector<HTMLElement>("[data-role=bg]");
      if (bg) {
        bg.style.backgroundImage = `url('${url.replace(/'/g, "%27")}')`;
        bg.classList.add("is-loaded");
      }
    };
    probe.src = url;
  });

  const leave = (captured: SplashCapture): void => {
    overlay.classList.add("wiz-splash--leaving");
    const done = (): void => { overlay.remove(); onStart(captured); };
    if (reduce) { done(); return; }
    window.setTimeout(done, 360);
  };

  overlay.querySelector<HTMLButtonElement>("[data-role=start]")?.addEventListener("click", () => {
    void openCapturePopup(branding, retailerId).then((captured) => {
      // Dismissed without completing — stay on the welcome screen. The wizard
      // is worth nothing to the retailer without a way to reach the customer.
      if (captured) leave(captured);
    });
  });

  host.appendChild(overlay);
  if (!reduce) requestAnimationFrame(() => overlay.classList.add("wiz-splash--in"));
}
